import { createContext, useContext, useEffect } from 'react';

/** What a screen is (screen.tsx): office and guest screens are CALM, counter and wall screens RUSH, a wall on the dark ground. */
export type ScreenKind = 'office' | 'counter' | 'wall' | 'guest';

/** The screen a control is drawn on; null on a page not moved to the kit yet (E07's legacy mark), which keeps the old look until it moves. */
export const ScreenContext = createContext<ScreenKind | null>(null);
export const useScreen = (): ScreenKind | null => useContext(ScreenContext);

/** Heading level by nesting: the Screen's title is h1, a Section in it h2, one inside that h3. */
export const Level = createContext<2 | 3 | 4>(2);

/** The heading level where a kit piece is drawn, by nesting (kit use only: index.ts does not export it). */
export const useLevel = (): 2 | 3 | 4 => useContext(Level);


/** What each kind of screen is drawn with: MIX's density, the ground, and the column's width outside the staff frame. */
export const FRAME = {
  office: { density: 'calm', ground: 'light', width: 'max-w-[40rem]' },
  guest: { density: 'calm', ground: 'light', width: 'max-w-[30rem]' },
  counter: { density: 'rush', ground: 'light', width: 'max-w-none' },
  wall: { density: 'rush', ground: 'dark', width: 'max-w-none' },
} as const satisfies Record<ScreenKind, { readonly density: 'calm' | 'rush'; readonly ground: 'light' | 'dark'; readonly width: string }>;

/** A screen's own palette over its kind's (kit.css): 'member', a member's own page. */
export type Look = 'member';

/** Sets the density, the ground and the look on <html> while a screen is shown (Radix overlays render outside the page and read the same tokens). Off (`on` false) for a page inside the staff frame, whose frame sets them while it stays. */
export function useFrame(kind: ScreenKind, look?: Look, on = true): void {
  const frame = FRAME[kind];
  useEffect(() => {
    if (!on) return;
    const html = document.documentElement;
    html.dataset['density'] = frame.density;
    html.dataset['ground'] = frame.ground;
    if (look !== undefined) html.dataset['look'] = look;
    return () => {
      delete html.dataset['density'];
      delete html.dataset['ground'];
      delete html.dataset['look'];
    };
  }, [frame, look, on]);
}
